import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState, useRef, useEffect, type FC } from 'react';
import { Camera, ChevronDown, Send, X } from 'lucide-react';
import { useStore, useUnreadChatMessageCount } from 'engine/store';
import { TabButton } from './rounds/$roundId';
import { getFileExtension, getPhotoUrl, useOnline } from 'engine/api';
import { BunnyMediaRenderer } from 'components/BunnyMediaRenderer';
import type { MediaNode, MessageAttachmentData } from 'shared/modelTypes/media';
import { resize } from 'engine/resizeImage';

export const Route = createFileRoute('/chat')({
	component: RouteComponent,
	beforeLoad: async () => {
		const { game } = useStore.getState();

		if (!game) {
			throw redirect({
				to: '/',
			});
		}

		if (!game.gameMasterChat && !game.globalChat) {
			// chat not supported!
			throw redirect({
				to: '/',
			});
		}
	},
});

function RouteComponent() {
	const isOnline = useOnline();

	const messages = useStore((state) => state.chatMessages);
	const submitChatMessage = useStore((state) => state.submitChatMessage);
	const teamId = useStore((state) => state.teamId);
	const eventId = useStore((state) => state.eventId);
	const teams = useStore((state) => state.teams);
	const activeChatTab = useStore((state) => state.activeChatTab);
	const readCount = useStore((state) => state.chatMessagesReadCount);
	const setReadCount = useStore((state) => state.setChatMessagesReadCount);
	const [chatIsFocusedBelow, setChatIsFocusedBelow] = useState(false);

	useEffect(() => {
		scrollToBottom();
	}, [activeChatTab]);

	useEffect(() => {
		if (chatIsFocusedBelow) {
			scrollToBottom();
		}
		if (chatIsFocusedBelow && readCount[activeChatTab] < messages[activeChatTab].length) {
			setReadCount(activeChatTab, messages[activeChatTab].length);
		}
	}, [messages, activeChatTab, readCount, setReadCount, chatIsFocusedBelow]);

	const getTeamName = (id?: string | null) => (id ? teams.find((team) => team.id === id)?.name : __`Game master`);

	const [inputValue, setInputValue] = useState('');
	const messagesEndRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' });
	};

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const target = e.target as HTMLDivElement;
		const isAtBottom = target.scrollHeight == target.clientHeight || target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
		setChatIsFocusedBelow(isAtBottom);
	};

	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [file, setFile] = useState<File | null>(null);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const [isUploading, setIsUploading] = useState(false);

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		setIsUploading(true);
		if (file) {
			setPreviewUrl(URL.createObjectURL(file));
			if (file.type.startsWith('image/')) {
				const resizedImage = await resize(file, {
					width: 2048,
					height: 2048,
				});
				setFile(resizedImage);
				setPreviewUrl(URL.createObjectURL(resizedImage));
			} else {
				const url = URL.createObjectURL(file);
				setPreviewUrl(url);
				setFile(file);
			}
			setIsUploading(false);
		}
	};

	const handleRemoveImage = () => {
		setPreviewUrl(null);
		setFile(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		// if (!inputValue.trim()) return;
		if (inputValue.trim().length < 1 && file == null) return;

		submitChatMessage({
			id: crypto.randomUUID(),
			message: inputValue,
			attachments: file != null ? [await uploadChatFile(file)] : undefined,
			channel: activeChatTab,
			_created_at: new Date(),
		});

		setInputValue('');
		handleRemoveImage();
		scrollToBottom();
	};

	return (
		<div className="flex h-full flex-col">
			<ChatNav />
			<div onScroll={handleScroll} className="flex-1 shrink space-y-2 overflow-y-auto p-4">
				{messages[activeChatTab].map((message, index) => (
					<div
						key={index}
						className={`p-2 skin-rounded-lg ${
							message.teamId === teamId ? 'ml-auto skin-bg-button skin-text-button' : 'skin-bg-button skin-text-button'
						} max-w-[80%]`}
					>
						<div className="text-sm">{getTeamName(message.teamId)}</div>
						<div>{message.message}</div>
						{message.attachments &&
							message.attachments.map((att) => {
								const fileExt = getFileExtension(att.filename);
								return (
									<div
										className="my-2 max-h-[60vh] min-h-40 min-w-56 max-w-[70vw] overflow-hidden rounded-xl md:max-w-[45vw]"
										key={att.id}
									>
										{att.mimeType?.startsWith('image/') && fileExt ? (
											<div className="checkerboard my-2 w-fit">
												<img
													src={getPhotoUrl(
														att.id,
														fileExt,
														message.teamId ?? 'game-master',
														eventId!,
														att.contentSource,
													)}
												/>
											</div>
										) : (
											<div className="size-full object-contain">
												{message.teamId}
												<BunnyMediaRenderer
													media={att as MediaNode}
													library={
														message.teamId == 'game-master'
															? import.meta.env.VITE_CDN
															: import.meta.env.VITE_CDN_GAME
													}
												/>
											</div>
										)}
									</div>
								);
							})}
					</div>
				))}
				<div ref={messagesEndRef} />
			</div>
			{chatIsFocusedBelow || (
				<div
					className="absolute bottom-16 right-5 m-2 flex cursor-pointer items-center rounded-full p-2 text-white shadow-lg skin-bg-button hover:from-[var(--theme-300)] hover:to-[var(--theme-400)]"
					onClick={scrollToBottom}
				>
					{messages[activeChatTab].length - readCount[activeChatTab] > 0 && (
						<div className="absolute -left-3 -top-3 flex items-center justify-center">
							<div className="absolute inset-0 z-0 animate-ping rounded-full border-2 border-gray-400"></div>
							<div className="relative z-10 flex size-6 items-center justify-center rounded-full border border-gray-200 bg-white text-xs text-gray-400">
								{messages[activeChatTab].length - readCount[activeChatTab]}
							</div>
						</div>
					)}
					<ChevronDown className="size-5" />
				</div>
			)}

			<div>
				{previewUrl && (
					<div className="p-1 pl-3">
						<div className="relative size-16 overflow-hidden rounded">
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									handleRemoveImage();
								}}
								className="absolute right-0 top-0 z-10 m-0.5 rounded-full bg-white/80 p-0 shadow hover:bg-white"
							>
								<X className="size-5 text-gray-950" />
							</button>
							{file?.type.startsWith('video/') && (
								<video src={previewUrl} className="size-full object-cover" loop controls></video>
							)}

							{file?.type.startsWith('image/') && <img src={previewUrl} alt="Preview" className="size-full object-cover" />}
						</div>
					</div>
				)}
			</div>
			<form onSubmit={handleSubmit} className="flex shrink-0 gap-2 p-2">
				<div className="relative">
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*,video/*"
						onChange={handleFileChange}
						className="hidden"
						disabled={isUploading}
					/>
				</div>
				{!previewUrl && (
					<div>
						<button
							type="button"
							onClick={(e) => {
								e.preventDefault();
								fileInputRef.current?.setAttribute('capture', 'environment');
								fileInputRef.current?.click();
							}}
							className="p-2 transition-colors skin-bg-button skin-text-button skin-rounded-md disabled:cursor-not-allowed disabled:opacity-50"
						>
							<Camera className="size-6" />
						</button>
					</div>
				)}
				<input
					type="text"
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					className="flex-1 border p-2 skin-bg-input skin-border-input skin-text-input skin-rounded-lg disabled:opacity-50"
					placeholder={__`Type a message...`}
					disabled={!isOnline}
				/>
				<button
					disabled={!isOnline}
					type="submit"
					className="p-2 transition-colors skin-bg-button skin-text-button skin-rounded-md disabled:cursor-not-allowed disabled:opacity-50"
				>
					<Send className="size-6" />
				</button>
			</form>
		</div>
	);
}

const ChatNav: FC = () => {
	const setActiveChatTab = useStore((s) => s.setActiveChatTab);
	const activeChatTab = useStore((s) => s.activeChatTab);
	const game = useStore((s) => s.game)!;
	const { unreadCountGameMaster, unreadCountGlobal } = useUnreadChatMessageCount();

	return (
		<div className="pt-2 shadow-md skin-bg">
			<div className="flex">
				{game.globalChat && (
					<TabButton
						onClick={() => setActiveChatTab('global')}
						active={activeChatTab === 'global'}
						label={__`Global`}
						count={unreadCountGlobal}
					/>
				)}
				{game.gameMasterChat && (
					<TabButton
						onClick={() => setActiveChatTab('game-master')}
						active={activeChatTab === 'game-master'}
						label={__`Game master`}
						count={unreadCountGameMaster}
					/>
				)}
			</div>
		</div>
	);
};

async function uploadChatFile(file: File): Promise<MessageAttachmentData> {
	const state = useStore.getState();
	const url = import.meta.env.VITE_WEBSOCKET_URL + '/upload-client';

	const formData = new FormData();
	if (file) {
		formData.append('file', file);
	}
	formData.append('teamId', state.teamId as string);
	formData.append('eventId', state.eventId as string);

	const res = (await fetch(url, {
		method: 'POST',
		body: formData,
	})
		.then((res) => res.json())
		.catch((e: unknown) => {
			console.error(e);
			// if (e instanceof TypeError) {
			// console.log('error message:', e.message);

			// }
			return { success: false };
		})) as {
		success: boolean;
		media?: {
			id: string;
			videoId?: string;
			kind: 'image' | 'audio' | 'text';
			mimeType: string;
			filename: string;
			path: string;
		};
	};

	if (res.success && res.media) {
		return {
			id: res.media.id,
			parentId: null,
			kind: res.media.kind,
			filename: res.media.filename,
			createdAt: '',
			updatedAt: '',
			videoId: res.media.videoId,
			mimeType: res.media.mimeType,
			contentSource: 'client',
		};
		//res.media;
	} else throw new Error('Media upload went wrong');
}
